import { useState } from "react";
import { Check, Copy, Undo2 } from "lucide-react";
import { MCP_URL } from "../lib/site";
import { Mark } from "./Mark";

/**
 * "Claude builds my model" (PRD §11). The conversation is the flagship flow from §11.1, shortened. Its numbers are
 * the worked example's illustrative outputs (§14.1), and the page says so.
 */
export function Claude() {
	return (
		<section id="claude" className="on-night relative overflow-hidden">
			<div className="relative mx-auto grid max-w-[1240px] gap-x-16 gap-y-14 px-5 py-24 md:px-8 lg:grid-cols-12 lg:py-32">
				<div className="min-w-0 lg:col-span-5">
					<h2 className="text-[clamp(2.25rem,4.4vw,3.5rem)] leading-[1.02] tracking-[-0.035em]">Describe the business. Claude builds the model.</h2>
					<p className="mt-6 text-night-text">
						ModelCurve has an MCP server. Connect it to Claude, write a paragraph about what worries you, and Claude creates the events, metrics, links and
						scenarios. Every change passes the same checks as the app, so a model Claude builds is a model the app can run.
					</p>
					<p className="mt-4 text-night-quiet">The whole grammar fits in 2,500 tokens. It does not take the largest model to write a valid one.</p>

					<ol className="mt-10 space-y-6">
						<Step n={1} title="Add ModelCurve to Claude">
							In Claude, add a custom connector with this address, then sign in.
							<CopyField value={MCP_URL} />
						</Step>
						<Step n={2} title="Describe the situation">
							In numbers you would defend: who pays you, what could go wrong, how likely you think it is.
						</Step>
						<Step n={3} title="Drag it into shape">
							Open the link Claude gives you. The model is a draft; your hands make it a forecast.
						</Step>
					</ol>

					<div className="mt-10 border-t border-night-line pt-6">
						<p className="text-sm text-night-quiet">Agents in a terminal use the command-line tool, over the same API:</p>
						<pre className="mt-3 overflow-x-auto rounded-md border border-night-line bg-night-raised px-4 py-3 font-mono text-[13px] leading-relaxed text-night-text">
							<span className="text-night-quiet">$ </span>modelcurve grammar{"\n"}
							<span className="text-night-quiet">$ </span>modelcurve apply --file draft.json --dry-run{"\n"}
							<span className="text-night-quiet">$ </span>modelcurve compare
						</pre>
					</div>
				</div>

				<div className="min-w-0 lg:col-span-7">
					<Conversation />
				</div>
			</div>
		</section>
	);
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
	return (
		<li className="grid grid-cols-[2rem_1fr] gap-x-3">
			<span className="tnum mt-[3px] flex h-6 w-6 items-center justify-center rounded-full border border-peri-tint/60 text-xs font-medium text-peri-tint">
				{n}
			</span>
			<div>
				<h3 className="text-base font-semibold">{title}</h3>
				<div className="mt-1 text-sm text-night-quiet">{children}</div>
			</div>
		</li>
	);
}

function CopyField({ value }: { value: string }) {
	const [copied, setCopied] = useState(false);
	return (
		<div className="mt-3 flex max-w-[26rem] items-stretch overflow-hidden rounded-sm border border-night-line bg-night-raised">
			<code className="flex-1 truncate px-3 py-2.5 font-mono text-[13px] text-night-text">{value}</code>
			<button
				type="button"
				onClick={() => {
					void navigator.clipboard?.writeText(value).then(() => {
						setCopied(true);
						window.setTimeout(() => setCopied(false), 1800);
					});
				}}
				className="flex min-w-[88px] items-center justify-center gap-1.5 border-l border-night-line px-3 text-xs font-medium text-night-text hover:bg-[#2a2a38]"
			>
				{copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
				{copied ? "Copied" : "Copy"}
			</button>
		</div>
	);
}

const TOOLS: [string, string][] = [
	["get_grammar", ""],
	["search_nodes", "“US recession”"],
	["search_nodes", "“AI crash”"],
	["create_node", "× 13"],
	["set_forecast", "× 13"],
	["add_link", "× 6"],
	["create_scenario", "“Acme exits”"],
	["set_in_scenario", "Assume Acme leaves"],
	["run", ""],
];

function Conversation() {
	return (
		<figure className="relative">
			<div className="overflow-hidden rounded-lg border border-night-line bg-night-raised shadow-[0_40px_80px_-30px_rgb(0_0_0/0.6)]">
				<div className="flex items-center justify-between border-b border-night-line px-5 py-3 text-xs text-night-quiet">
					<span className="font-medium text-night-text">Claude</span>
					<span className="flex items-center gap-1.5 rounded-sm border border-night-line px-2 py-1">
						<Mark size={13} /> ModelCurve connected
					</span>
				</div>

				<div className="space-y-6 px-5 py-6 sm:px-7">
					<div className="ml-auto max-w-[34rem] rounded-md bg-[#2a2a38] px-4 py-3 text-[15px] leading-relaxed text-night-text">
						Model my firm’s revenue risk. We’re 40 people at $150k each, other costs $1.2M, target margin 5%. Acme is $2.4M a year and I’d put 25% on them
						leaving by end of next year, more in a recession. Globex is $1.6M and would halve its spend in an AI crash. Initech is $1M and tends to follow Acme.
						How likely is it that I’ll need to cut 10% of staff, and when?
					</div>

					<div className="rounded-md border border-night-line">
						<div className="border-b border-night-line px-4 py-2 text-xs text-night-quiet">Used ModelCurve</div>
						<ul className="grid gap-x-6 gap-y-1.5 px-4 py-3 font-mono text-[12.5px] sm:grid-cols-2">
							{TOOLS.map(([tool, arg], i) => (
								<li key={i} className="flex items-baseline gap-2 truncate">
									<Check size={12} className="shrink-0 translate-y-[1px] text-peri-tint" aria-hidden />
									<span className="text-night-text">{tool}</span>
									{arg && <span className="truncate text-night-quiet">{arg}</span>}
								</li>
							))}
						</ul>
					</div>

					<div className="max-w-[36rem] space-y-3 text-[15px] leading-relaxed text-night-text">
						<p>
							<strong className="font-semibold text-white">29% chance you need to cut 10% or more of staff by Dec 2027.</strong> If it happens, around Sep 2027,
							and about 12 people.
						</p>
						<p>
							Acme matters most, by a distance. If you assume Acme leaves, the chance rises to 96%. Initech following Acme and Globex come next. I added a US
							recession and an AI crash as events and linked them to your clients, so a change in either reaches this number.
						</p>
						<a
							href="#scenarios"
							className="mt-1 flex items-center gap-3 rounded-md border border-night-line bg-night px-4 py-3 no-underline hover:border-night-quiet"
						>
							<Mark size={22} />
							<span className="min-w-0">
								<span className="block text-sm font-medium text-white">Layoffs needed</span>
								<span className="block truncate text-xs text-night-quiet">Open in ModelCurve, with 2 scenarios</span>
							</span>
						</a>
					</div>
				</div>

				{/* The app's side of it: one change set, one undo. */}
				<div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-night-line bg-night px-5 py-3 text-sm sm:px-7">
					<span>
						<span className="font-medium text-white">Built with Claude</span> <span className="text-night-quiet">· 36 changes in one change set</span>
					</span>
					<span className="flex items-center gap-1.5 text-night-quiet sm:ml-auto">
						<Undo2 size={14} aria-hidden /> Undo the session in one click
					</span>
				</div>
			</div>

			<figcaption className="mt-4 px-1 text-xs text-night-quiet">An illustration: the numbers are invented, the tools are real.</figcaption>
		</figure>
	);
}
